import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  getWhatsAppFlows,
  sendWhatsAppFlow,
  getApprovedWhatsAppTemplates,
  sendWhatsAppTemplateToLeads
} from '../../services/api/implementation/impl';
import {
  WA_SEND_CHUNK_SIZE,
  chunkArray,
  formatBulkSendToast,
  summarizeChunkResults
} from '../../utils/bulkWhatsAppSendUtils';

function resolveFlowApprovalStatus(flow) {
  return String(
    flow?.template_approval_status ?? flow?.template_status ?? flow?.approval_status ?? ''
  ).toUpperCase();
}

function isFlowSendable(flow) {
  const status = resolveFlowApprovalStatus(flow);
  return status === 'APPROVED' || status === 'ACTIVE';
}

function buildFlowSendPayload({ isPreLeadList, preLeadListId, chunkIds }) {
  if (isPreLeadList) {
    return {
      recipient_type: 'pre_lead_list',
      pre_lead_list_id: Number(preLeadListId),
      pre_lead_ids: chunkIds,
      variables: {},
      skip_template_if_session_open: false
    };
  }
  return {
    recipient_type: 'lead',
    lead_ids: chunkIds,
    variables: {},
    skip_template_if_session_open: false
  };
}

/**
 * Modal to send either a WhatsApp flow or an approved Pinggo WA template.
 * Template mode is available for leads only (not prelead lists).
 */
const BulkSendFlowModal = ({
  projectId: projectIdProp = null,
  recipientType = 'lead',
  leadIds = [],
  preLeadListId = null,
  preLeadIds = [],
  onClose,
  onSent
}) => {
  const isPreLeadList = recipientType === 'pre_lead_list';
  const recipientIds = isPreLeadList ? preLeadIds : leadIds;
  const recipientLabel = isPreLeadList ? 'prelead' : 'lead';
  const recipientLabelPlural = isPreLeadList ? 'preleads' : 'leads';
  const canUseTemplates = !isPreLeadList;

  const [sendKind, setSendKind] = useState('flow'); // 'flow' | 'template'
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [pickedProjectId, setPickedProjectId] = useState(
    projectIdProp != null ? String(projectIdProp) : ''
  );
  const [isSending, setIsSending] = useState(false);
  const [sentProgress, setSentProgress] = useState(0);
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTemplateSearch(templateSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [templateSearch]);

  const { projectsForDropdown = [], isProjectsLoading } = useProjectsList();

  const projectId = useMemo(() => {
    if (projectIdProp != null && Number(projectIdProp) > 0) return Number(projectIdProp);
    const n = Number(pickedProjectId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [projectIdProp, pickedProjectId]);

  const needsProjectPicker = !(projectIdProp != null && Number(projectIdProp) > 0);
  const isTemplateMode = canUseTemplates && sendKind === 'template';

  const {
    data: flows = [],
    isLoading: isLoadingFlows,
    isError: isFlowsError,
    refetch: refetchFlows
  } = useQuery({
    queryKey: ['whatsappFlows', projectId],
    queryFn: () => getWhatsAppFlows(projectId),
    enabled: Boolean(projectId) && !isTemplateMode,
    staleTime: 60_000
  });

  const {
    data: templatePayload,
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
    isError: isTemplatesError,
    refetch: refetchTemplates
  } = useQuery({
    queryKey: ['approvedWhatsAppTemplates', projectId, debouncedTemplateSearch],
    queryFn: () =>
      getApprovedWhatsAppTemplates(projectId, { search: debouncedTemplateSearch || undefined }),
    enabled: Boolean(projectId) && isTemplateMode,
    staleTime: 60_000,
    placeholderData: keepPreviousData
  });

  const sendableFlows = useMemo(() => flows.filter(isFlowSendable), [flows]);
  const approvedTemplates = useMemo(
    () => (Array.isArray(templatePayload?.templates) ? templatePayload.templates : []),
    [templatePayload]
  );

  const selectedFlow = useMemo(
    () => sendableFlows.find((flow) => String(flow.id) === String(selectedFlowId)) || null,
    [sendableFlows, selectedFlowId]
  );
  const selectedTemplate = useMemo(
    () =>
      approvedTemplates.find(
        (t) => String(t.templateName) === String(selectedTemplateName)
      ) || null,
    [approvedTemplates, selectedTemplateName]
  );

  const totalRecipients = recipientIds.length;
  const needsChunking = totalRecipients > WA_SEND_CHUNK_SIZE;
  const showListSpinner = isTemplateMode
    ? isLoadingTemplates && !templatePayload
    : isLoadingFlows && flows.length === 0;
  const isError = isTemplateMode ? isTemplatesError : isFlowsError;
  const canSubmit = isTemplateMode ? Boolean(selectedTemplate) : Boolean(selectedFlow);

  const handleClose = () => {
    if (isSending) return;
    onClose?.();
  };

  const handleSend = async () => {
    if (isSending || recipientIds.length === 0) return;
    if (isPreLeadList && !(Number(preLeadListId) > 0)) {
      toast.error('Missing prelead list.');
      return;
    }
    if (isTemplateMode) {
      if (!selectedTemplate || !projectId) {
        toast.error('Please select a WhatsApp template to send.');
        return;
      }
    } else if (!selectedFlow) {
      toast.error('Please select a flow to send.');
      return;
    }

    const chunks = chunkArray(recipientIds, WA_SEND_CHUNK_SIZE);
    setIsSending(true);
    setSentProgress(0);

    let okTotal = 0;
    let failTotal = 0;
    let processed = 0;
    let hardError = null;

    try {
      for (const chunk of chunks) {
        try {
          let response;
          if (isTemplateMode) {
            response = await sendWhatsAppTemplateToLeads({
              projectId,
              leadIds: chunk,
              templateName: selectedTemplate.templateName
            });
          } else {
            response = await sendWhatsAppFlow(
              selectedFlow.id,
              buildFlowSendPayload({ isPreLeadList, preLeadListId, chunkIds: chunk })
            );
          }
          const { ok, fail } = summarizeChunkResults(response, {
            chunkSize: chunk.length,
            chunkAssumedOk: true
          });
          okTotal += ok;
          failTotal += fail;
        } catch (err) {
          failTotal += chunk.length;
          hardError = err;
        }
        processed += chunk.length;
        setSentProgress(processed);
      }

      const toastMsg = formatBulkSendToast({
        ok: okTotal,
        fail: failTotal,
        total: totalRecipients,
        labelSingular: recipientLabel,
        labelPlural: recipientLabelPlural
      });

      if (failTotal === 0) {
        toast.success(toastMsg);
      } else if (okTotal === 0) {
        const apiMsg =
          hardError?.response?.data?.message || hardError?.message || null;
        toast.error(apiMsg ? `${toastMsg} ${apiMsg}` : toastMsg);
      } else {
        toast.error(toastMsg);
      }

      onSent?.();
      onClose?.();
    } finally {
      setIsSending(false);
      setSentProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Send WhatsApp</p>
            <p className="truncate text-xs text-gray-500">
              To {totalRecipients} selected{' '}
              {totalRecipients > 1 ? recipientLabelPlural : recipientLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            aria-label="Close">
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {canUseTemplates ? (
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                disabled={isSending}
                onClick={() => setSendKind('flow')}
                className={`rounded-lg px-2 py-2 text-xs font-semibold ${
                  sendKind === 'flow'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Flow
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={() => setSendKind('template')}
                className={`rounded-lg px-2 py-2 text-xs font-semibold ${
                  sendKind === 'template'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                WA template
              </button>
            </div>
          ) : null}

          {needsProjectPicker ? (
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                Select a project
              </label>
              {isProjectsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <select
                  value={pickedProjectId}
                  onChange={(e) => {
                    setPickedProjectId(e.target.value);
                    setSelectedFlowId('');
                    setSelectedTemplateName('');
                  }}
                  disabled={isSending}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                  <option value="" disabled>
                    Choose a project…
                  </option>
                  {projectsForDropdown.map((project) => (
                    <option key={project.value ?? project.id} value={project.value ?? project.id}>
                      {project.label ?? project.name ?? `Project ${project.value ?? project.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : null}

          {!projectId ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {needsProjectPicker
                ? `Select a project to load WhatsApp ${isTemplateMode ? 'templates' : 'flows'} for these ${recipientLabelPlural}.`
                : `These ${recipientLabelPlural} aren’t linked to a single project, so WhatsApp options can’t be listed.`}
            </p>
          ) : showListSpinner ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <p className="text-sm text-gray-600">
                Couldn’t load {isTemplateMode ? 'templates' : 'flows'}.
              </p>
              <button
                type="button"
                onClick={() => (isTemplateMode ? refetchTemplates() : refetchFlows())}
                className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2A565E]/90">
                Try again
              </button>
            </div>
          ) : isTemplateMode && approvedTemplates.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {debouncedTemplateSearch
                ? 'No approved templates match this search.'
                : 'No approved Pinggo WhatsApp templates available for this project.'}
            </p>
          ) : !isTemplateMode && sendableFlows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No approved flows available for this project. Only flows whose opener template is
              approved by Meta can be sent.
            </p>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                  {isTemplateMode ? 'Select a WA template' : 'Select a flow'}
                </label>
                {isTemplateMode ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="search"
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        disabled={isSending}
                        placeholder="Search templates…"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50"
                      />
                      {isFetchingTemplates && templatePayload ? (
                        <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-gray-400" />
                      ) : null}
                    </div>
                    <select
                      value={selectedTemplateName}
                      onChange={(e) => setSelectedTemplateName(e.target.value)}
                      disabled={isSending}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                      <option value="" disabled>
                        Choose a template…
                      </option>
                      {approvedTemplates.map((tpl) => (
                        <option key={tpl.templateName} value={tpl.templateName}>
                          {tpl.templateName}
                          {tpl.language ? ` (${tpl.language})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <select
                    value={selectedFlowId}
                    onChange={(e) => setSelectedFlowId(e.target.value)}
                    disabled={isSending}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                    <option value="" disabled>
                      Choose a flow…
                    </option>
                    {sendableFlows.map((flow) => (
                      <option key={flow.id} value={flow.id}>
                        {flow.name || 'Untitled flow'}
                      </option>
                    ))}
                  </select>
                )}

                {!isTemplateMode && selectedFlow?.description ? (
                  <p className="mt-2 text-xs text-gray-400">{selectedFlow.description}</p>
                ) : null}
                {isTemplateMode && selectedTemplate?.category ? (
                  <p className="mt-2 text-xs text-gray-400">
                    {selectedTemplate.category}
                    {selectedTemplate.language ? ` · ${selectedTemplate.language}` : ''}
                  </p>
                ) : null}
              </div>

              {isSending ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">
                    Sent {sentProgress}/{totalRecipients}…
                  </p>
                  <p className="mt-1 text-xs text-amber-800">
                    Keep this page open until sending finishes
                  </p>
                  {needsChunking ? (
                    <p className="mt-1 text-[11px] text-amber-700">
                      Sending in batches of {WA_SEND_CHUNK_SIZE}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || !canSubmit}
                  className="flex items-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2A565E]/90 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2} />
                  )}
                  {isSending ? `Sent ${sentProgress}/${totalRecipients}` : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkSendFlowModal;
